import React, { useState, useRef, useEffect } from "react";
import { z } from "zod";
import Button from "../common/Button";
import { FunnelIcon } from "@heroicons/react/24/solid";
import Select from "../common/Select";
import PartyListPagination from "../party/PartyList";
import apiService from "../../services/ApiService";
import { apiRoutes } from "../../shared/apiRoutes";

interface QuickAddPartyFormData {
  code: string;
  name: string;
  openingBalance: string;
}

// Validation schema matching CreateParty
const partyCodeValidation = z
  .string({
    required_error: "Party code is required"
  })
  .min(1, "Party code must be 1 characters")
  .max(5, "Party code must be 5 characters")
  .regex(/^[a-zA-Z0-9]+$/, "Party code must be alphanumeric");

const quickAddPartySchema = z.object({
  code: partyCodeValidation,
  name: z.string({ required_error: "Party name is required" }).min(1, "Party name must be at least 1 character").max(60),
  openingBalance: z.coerce.number().min(-99999999999).max(99999999999),
});

const QuickAddParty: React.FC = () => {
  const [formData, setFormData] = useState<QuickAddPartyFormData>({
    code: "",
    name: "",
    openingBalance: "0",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof QuickAddPartyFormData, string>>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Key to force PartyList refresh

  const codeInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const balanceInputRef = useRef<HTMLInputElement>(null);

  // Focus first input on mount
  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  const handleChange = (field: keyof QuickAddPartyFormData, value: string) => {
    let processedValue = value;

    // Process openingBalance to allow digits and negative sign
    if (field === "openingBalance") {
      if (value === '' || value === '-') {
        // Allow empty or just "-" for negative numbers being typed
        processedValue = value;
      } else {
        // Allow digits and minus sign at the start
        // Remove any characters that aren't digits or a leading minus sign
        const cleaned = value.replace(/[^0-9-]/g, '');
        // Ensure minus sign only appears at the start
        if (cleaned.includes('-')) {
          const parts = cleaned.split('-');
          if (parts[0] === '') {
            // Minus at start is valid
            processedValue = '-' + parts.slice(1).join('').replace(/[^0-9]/g, '');
          } else {
            // Minus not at start, remove it
            processedValue = cleaned.replace(/-/g, '');
          }
        } else {
          processedValue = cleaned;
        }
      }
    }

    // Convert partyCode and partyName to uppercase
    if (field === "code" || field === "name") {
      processedValue = value.toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateField = (field: keyof QuickAddPartyFormData, value: string): boolean => {
    try {
      const fieldSchema = quickAddPartySchema.shape[field];
      if (fieldSchema) {
        fieldSchema.parse(value);
      }
      return true;
    } catch (error: any) {
      if (error.errors && error.errors[0]) {
        setErrors((prev) => ({
          ...prev,
          [field]: error.errors[0].message,
        }));
      }
      return false;
    }
  };

  const validateForm = (): boolean => {
    const validation = quickAddPartySchema.safeParse({
      code: formData.code,
      name: formData.name,
      openingBalance: formData.openingBalance,
    });

    if (!validation.success) {
      const newErrors: Partial<Record<keyof QuickAddPartyFormData, string>> = {};
      validation.error.errors.forEach((err) => {
        const field = err.path[0] as keyof QuickAddPartyFormData;
        newErrors[field] = err.message;
      });
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      // Prepare request payload - convert to uppercase and trim
      const requestPayload = {
        partyCode: formData.code.toUpperCase().trim(),
        partyName: formData.name.toUpperCase().trim(),
        city: "",
        remarks: "",
        phone: "",
        openingBalance: Number(formData.openingBalance),
        vatav: 0,
        partners: [],
        thirdPartyId: null,
        sessionPartyCommission: 0,
        sessionThirdPartyCommission: 0,
        oneDayPartyCommission: 0,
        oneDayThirdPartyCommission: 0,
        testPartyCommission: 0,
        testThirdPartyCommission: 0,
      };

      await apiService.post(apiRoutes.parties.create, requestPayload);

      // Show success message
      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: { message: "Party created successfully", type: "success" },
        })
      );

      // Reset form
      setFormData({
        code: "",
        name: "",
        openingBalance: "0",
      });
      setErrors({});

      // Refresh the party list by incrementing refreshKey
      setRefreshKey((prev) => prev + 1);

      // Focus back to first input
      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 100);
    } catch (error: any) {
      console.error("Error creating party:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create party";
      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: { message: errorMessage, type: "error" },
        })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: keyof QuickAddPartyFormData
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (field === "code") {
        nameInputRef.current?.focus();
      } else if (field === "name") {
        balanceInputRef.current?.focus();
      } else if (field === "openingBalance") {
        // Save on Enter from balance field
        handleSubmit(e as any);
      }
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Header - Same as PartyList */}
      <div className="flex justify-between items-center p-1 bg-gray-100 rounded-sm rounded-b-none flex-shrink-0">
        <h2 className="text-left text-gray-800 font-bold">Quick Add Party</h2>
        <h2 className="text-left text-gray-800 font-bold">
          Rows: <Select options={[]} onChange={() => {}} />
        </h2>
        <div className="flex items-center space-x-2 text-right text-gray-800">
          <FunnelIcon className="h-5 w-5 text-gray-700 hover:cursor-pointer opacity-0 pointer-events-none" />
        </div>
      </div>

      {/* Party List - Above the form */}
      <div className="flex-1 overflow-hidden min-h-0">
        <PartyListPagination refreshKey={refreshKey} />
      </div>

      {/* Form - Stuck to bottom, reduced height */}
      <div className="flex-shrink-0 border-t border-gray-300 bg-white">
        <form
          onSubmit={handleSubmit}
          className="p-2"
        >
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
            {/* Party Code */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Party Code<span className="text-red-500">*</span>
              </label>
              <input
                ref={codeInputRef}
                type="text"
                className={`w-full p-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.code ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Code"
                value={formData.code}
                onChange={(e) => handleChange("code", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, "code")}
                onBlur={() => validateField("code", formData.code)}
                required
                maxLength={5}
              />
              {errors.code && (
                <div className="text-red-500 text-xs mt-0.5">{errors.code}</div>
              )}
            </div>

            {/* Party Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Party Name<span className="text-red-500">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                className={`w-full p-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, "name")}
                onBlur={() => validateField("name", formData.name)}
                required
                maxLength={60}
              />
              {errors.name && (
                <div className="text-red-500 text-xs mt-0.5">{errors.name}</div>
              )}
            </div>

            {/* Opening Balance */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Opening Balance
              </label>
              <input
                ref={balanceInputRef}
                type="text"
                className={`w-full p-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.openingBalance
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Balance"
                value={formData.openingBalance}
                onChange={(e) => handleChange("openingBalance", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, "openingBalance")}
                onFocus={(e) => {
                  // If value is "0", select all text so user can easily replace it
                  if (e.target.value === "0") {
                    e.target.select();
                  }
                }}
                onBlur={() =>
                  validateField("openingBalance", formData.openingBalance)
                }
              />
              {errors.openingBalance && (
                <div className="text-red-500 text-xs mt-0.5">
                  {errors.openingBalance}
                </div>
              )}
            </div>

            {/* Save Button - Same line */}
            <div className="flex items-end">
              <Button
                type="submit"
                disabled={isProcessing}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50"
              >
                {isProcessing ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAddParty;
